import { PERFECT_SCORE, WORST_SCORE, type SpentStep } from "../../shared/domain.ts";
import type { DailyResult } from "./storage.ts";

/**
 * The Share Card — the thing that actually makes the game spread.
 *
 * Spoiler-free by construction: it is built only from Verdicts, so no title or
 * artist can leak into it even by accident. It has to read well pasted as plain
 * text into a chat app, because that is the only place it will ever be seen.
 *
 * Squares follow CONTEXT.md — green Exact, yellow Artist Match, grey Miss —
 * plus a distinct black square for a Skip.
 */

const SQUARE: Record<SpentStep["verdict"], string> = {
  exact: "🟩",
  "artist-match": "🟨",
  miss: "⬜",
  skipped: "⬛",
};

export function rowFor(verdicts: SpentStep["verdict"][]): string {
  return verdicts.map((v) => SQUARE[v]).join("");
}

export function shareText(result: DailyResult, streak: number, origin?: string): string {
  const lines = [`🎵 Spotiguessr ${result.date} — ${result.score}/${WORST_SCORE}`, ""];

  for (const verdicts of result.verdicts) lines.push(rowFor(verdicts));

  lines.push("");

  if (result.score === PERFECT_SCORE) {
    lines.push("Perfect set 🔥");
  }
  if (streak > 1) {
    lines.push(`🔥 ${streak} day streak`);
  }
  if (origin) lines.push(origin);

  // Trailing blanks read as broken formatting in a chat bubble.
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trimEnd();
}

/**
 * Copies via the Web Share sheet on mobile and the clipboard elsewhere.
 * Returns how it went so the UI can confirm — a silent copy feels broken.
 */
export async function share(text: string): Promise<"shared" | "copied" | "failed"> {
  if (typeof navigator !== "undefined" && "share" in navigator) {
    try {
      await navigator.share({ text });
      return "shared";
    } catch (err) {
      // A user dismissing the share sheet is not a failure worth reporting.
      if (err instanceof Error && err.name === "AbortError") return "failed";
    }
  }

  try {
    await navigator.clipboard.writeText(text);
    return "copied";
  } catch {
    return "failed";
  }
}
